import React, { useMemo } from 'react'
import { DashboardInvestmentTransactions } from '../../../lib/types/propsType_v1'
import {
  formatTransactionAmountDisplay,
  formatTransactionQuantityDisplay,
  formatTransactionType,
  isSellTransaction,
} from '../dashboard-v2.utils'

type TransactionsTableProps = {
  transactions: DashboardInvestmentTransactions[]
  onTransactionClick?: (transaction: DashboardInvestmentTransactions) => void
}

const isBuyTransaction = (transactionType: string) => transactionType?.toUpperCase() === 'BUY'

const openDocumentDownload = (downloadLink: string) => {
  window.open(downloadLink, '_blank', 'noopener,noreferrer')
}

const IconDownload = () => (
  <svg viewBox='0 0 20 20' fill='none' aria-hidden focusable='false'>
    <path d='M10 3v9' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
    <path
      d='M6.5 8.5 10 12l3.5-3.5'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4 14.5v1A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5v-1'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
    />
  </svg>
)

export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  onTransactionClick,
}) => {
  const hasPortfolioId = useMemo(
    () =>
      Array.isArray(transactions) &&
      transactions.some((t) => !!t.portfolio_id && t.portfolio_id !== '-'),
    [transactions],
  )

  const hasTermsheetDownload = useMemo(
    () =>
      Array.isArray(transactions) &&
      transactions.some(
        (t) => isBuyTransaction(t.transaction_type) && Boolean(t.termsheet?.downloadLink),
      ),
    [transactions],
  )

  const hasAllotmentConfirmationDownload = useMemo(
    () =>
      Array.isArray(transactions) &&
      transactions.some(
        (t) =>
          isBuyTransaction(t.transaction_type) && Boolean(t.allotmentConfirmation?.downloadLink),
      ),
    [transactions],
  )

  const hasQuantity = useMemo(
    () => Array.isArray(transactions) && transactions.some((t) => t.quantity !== null),
    [transactions],
  )

  // Share-priced orders show shares (not units); mixed lists use a combined header.
  const quantityHeader = useMemo(() => {
    const rows = Array.isArray(transactions) ? transactions : []
    const hasShares = rows.some((t) => Boolean(t.shares))
    const hasUnits = rows.some((t) => t.quantity !== null && t.quantity !== undefined && !t.shares)
    if (hasShares && hasUnits) return 'Units / Shares'
    return hasShares ? 'Shares' : 'Units'
  }, [transactions])

  const isClickable = typeof onTransactionClick === 'function'

  return (
    <section className='dashboard-v2__table-card'>
      <h2>Transactions</h2>
      <div className='dashboard-v2__table-scroll'>
        <table>
          <thead>
            <tr>
              <th>Opportunity</th>
              <th>Date</th>
              <th>Type</th>
              <th>Amount</th>
              {hasQuantity ? <th>{quantityHeader}</th> : null}
              <th>Status</th>
              {hasPortfolioId && <th>Portfolio ID</th>}
              <th>Order ID</th>
              {hasTermsheetDownload && <th className='dashboard-v2__doc-col'>Termsheet</th>}
              {hasAllotmentConfirmationDownload && (
                <th className='dashboard-v2__doc-col'>Allotment</th>
              )}
            </tr>
          </thead>
          <tbody>
            {transactions?.map((t, idx) => (
              <tr
                key={`${t.opportunity_title}-${t.transaction_date}-${idx}`}
                className={isClickable ? 'dashboard-v2__transaction-row' : undefined}
                onClick={isClickable ? () => onTransactionClick(t) : undefined}
                onKeyDown={
                  isClickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onTransactionClick(t)
                        }
                      }
                    : undefined
                }
                tabIndex={isClickable ? 0 : undefined}
                aria-label={
                  isClickable
                    ? `View details for ${t.opportunity_title || 'transaction'}`
                    : undefined
                }
                data-testid={`transaction-row-${idx}`}
              >
                <td>{t.opportunity_title}</td>
                <td>{new Date(t.transaction_date).toLocaleDateString()}</td>
                <td>{formatTransactionType(t.transaction_type)}</td>
                <td
                  className={
                    isSellTransaction(t.transaction_type)
                      ? 'dashboard-v2__transaction-amount dashboard-v2__transaction-amount--sell'
                      : 'dashboard-v2__transaction-amount'
                  }
                >
                  {formatTransactionAmountDisplay(t.amount, t.transaction_type)}
                </td>
                {hasQuantity ? (
                  <td data-testid={`transaction-units-${idx}`}>
                    {formatTransactionQuantityDisplay(t.quantity)}
                  </td>
                ) : null}
                <td>
                  <span className='chip chip-completed'>Completed</span>
                </td>
                {hasPortfolioId && <td>{t.portfolio_id || '-'}</td>}
                <td>{t.order_id || '-'}</td>
                {hasTermsheetDownload && (
                  <td className='dashboard-v2__doc-col'>
                    {isBuyTransaction(t.transaction_type) && t.termsheet?.downloadLink ? (
                      <button
                        type='button'
                        className='dashboard-v2__termsheet-download-btn'
                        onClick={(e) => {
                          e.stopPropagation()
                          openDocumentDownload(t.termsheet!.downloadLink!)
                        }}
                        data-testid={`termsheet-download-${idx}`}
                        aria-label='Download termsheet'
                        title='Download termsheet'
                      >
                        <IconDownload />
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                )}
                {hasAllotmentConfirmationDownload && (
                  <td className='dashboard-v2__doc-col'>
                    {isBuyTransaction(t.transaction_type) &&
                    t.allotmentConfirmation?.downloadLink ? (
                      <button
                        type='button'
                        className='dashboard-v2__termsheet-download-btn'
                        onClick={(e) => {
                          e.stopPropagation()
                          openDocumentDownload(t.allotmentConfirmation!.downloadLink!)
                        }}
                        data-testid={`allotment-confirmation-download-${idx}`}
                        aria-label='Download allotment confirmation letter'
                        title='Download allotment confirmation letter'
                      >
                        <IconDownload />
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                )}
              </tr>
            )) || null}
          </tbody>
        </table>
      </div>
    </section>
  )
}
